import { useState } from 'react';
import type { ReactNode } from 'react';
import type { EnhancedStore } from '@reduxjs/toolkit';
import { apiClient } from '@clareefai/api';
import { bugReporterActionLog } from '@clareefai/utils';
import type { BugReportPayload, BugReportResponse } from '@clareefai/types';
import { toast } from '../toast';
import { captureConnectionInfo, captureLocalStorage } from './captureUtils';
import { BugReportModal } from './BugReportModal';
import { useBugReporterListeners } from './useBugReporterListeners';
import { isBugReporterEnabled } from './trigger';

const RTK_QUERY_CACHE_KEY = 'api';

function stripRtkQueryCache(state: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (key.endsWith(RTK_QUERY_CACHE_KEY) || key.endsWith('Api')) {
      result[key] = '[RTK Query cache omitted]';
    } else {
      result[key] = value;
    }
  }
  return result;
}

interface BugReporterProviderProps {
  appName: string;
  store: EnhancedStore;
  children: ReactNode;
}

function BugReporterInner({ appName, store, children }: BugReporterProviderProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTrigger = (capturedScreenshot: string) => {
    setScreenshot(capturedScreenshot);
    setModalOpen(true);
  };

  useBugReporterListeners({ onTrigger: handleTrigger });

  const handleSubmit = async (data: { title: string; description?: string; priority: string }) => {
    setIsSubmitting(true);
    try {
      const payload: BugReportPayload = {
        title: data.title,
        description: data.description ?? '',
        priority: data.priority as BugReportPayload['priority'],
        screenshot: screenshot ?? '',
        actionLog: bugReporterActionLog.getEntries(),
        reduxState: stripRtkQueryCache(store.getState() as Record<string, unknown>),
        localStorage: captureLocalStorage(),
        connectionInfo: captureConnectionInfo(),
        appName,
        reportedBy: localStorage.getItem('firebase_user_email') ?? 'unknown',
      };

      const response = await apiClient.post<BugReportResponse>('/bug-reports', payload);
      toast.success(`Bug reported: ${response.data.jiraKey}`);
      setModalOpen(false);
      setScreenshot(null);
    } catch (err) {
      console.error('Bug reporter: failed to submit', err);
      toast.error('Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {children}
      <BugReportModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setScreenshot(null); }}
        onSubmit={handleSubmit}
        screenshotPreview={screenshot}
        isSubmitting={isSubmitting}
      />
    </>
  );
}

export function BugReporterProvider({ appName, store, children }: BugReporterProviderProps) {
  if (!isBugReporterEnabled) return <>{children}</>;
  return <BugReporterInner appName={appName} store={store} children={children} />;
}
